import { Form } from '@ui';
import { Divider } from '@ds';
import { FormActions } from '@features/forms/FormActions/FormActions';
import { fromZonedTime } from 'date-fns-tz';
import { toast } from 'react-toastify';
import { OCSTimeFields } from '../components/OCSTimeFields';
import { CaseFacility, Randomization } from '@/api/apis/case';
import { useOffOcs } from '@/queries/case';
import { Uuid } from '@/api/types';
import { retrieveUnifiedError } from '@/api/utils';
import { SequentialLungFlushForm } from '../types';
import { getSequentialFlushDefaultValues, sequentialFlushSchema } from './config';
import { isRandomizationScs } from '@/utils/helpers';

type OffOCSSequentialFormProps = {
  caseId: Uuid;
  ocsStartTimestamp?: string;
  txCenter?: CaseFacility;
  randomization?: Randomization;
  onOpenChange: () => void;
};

export const OffOCSSequentialForm = ({
  caseId,
  ocsStartTimestamp,
  txCenter,
  randomization,
  onOpenChange,
}: OffOCSSequentialFormProps) => {
  const { mutateAsync } = useOffOcs();
  const isScs = isRandomizationScs(randomization);

  const handleSubmit = async (values: SequentialLungFlushForm) => {
    const { firstLungDate, firstLungTime, firstLungTimeZone, secondLungDate, secondLungTime, secondLungTimeZone } =
      values;
    const firstLungTimestamp = fromZonedTime(new Date(`${firstLungDate} ${firstLungTime}`), firstLungTimeZone);
    const secondLungTimestamp = fromZonedTime(new Date(`${secondLungDate} ${secondLungTime}`), secondLungTimeZone);
    try {
      await mutateAsync({
        caseId,
        data: {
          perfusionTerminationDataItem: {
            sequentialLungFlush: {
              firstLungFlushTime: { timestamp: firstLungTimestamp.toISOString(), timeZone: firstLungTimeZone },
              secondLungFlushTime: { timestamp: secondLungTimestamp.toISOString(), timeZone: secondLungTimeZone },
            },
          },
        },
      });
      onOpenChange();
      toast.success('Status successfully updated');
    } catch (error) {
      const { message } = retrieveUnifiedError(error);
      toast.error(message);
    }
  };

  const defaultValues = getSequentialFlushDefaultValues(txCenter, ocsStartTimestamp);

  return (
    <Form defaultValues={defaultValues} schema={sequentialFlushSchema} onSubmit={handleSubmit}>
      <div className="flex flex-col gap-6">
        <div className="flex gap-10">
          <OCSTimeFields
            heading="First Lung"
            subHeading="Flush Time"
            dateFieldName="firstLungDate"
            timeFieldName="firstLungTime"
            timezoneFieldName="firstLungTimeZone"
            ocsStartTime={ocsStartTimestamp}
            showPerfusionTime={!isScs}
          />
          <OCSTimeFields
            heading="Second Lung"
            subHeading="Flush Time"
            dateFieldName="secondLungDate"
            timeFieldName="secondLungTime"
            timezoneFieldName="secondLungTimeZone"
            ocsStartTime={ocsStartTimestamp}
            showPerfusionTime={!isScs}
          />
        </div>
        <Divider />
        <div className="flex justify-end gap-2">
          <FormActions
            actions={{
              size: 'md',
              discard: {
                label: 'Discard',
                handler: onOpenChange,
              },
              submit: {
                label: 'Update status',
              },
            }}
          />
        </div>
      </div>
    </Form>
  );
};
